import React, { useEffect, useState } from 'react';
import { Box, TextField, Fab, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { IAppointment, AppointmentUpdateNoteRequest } from "@ontime/types";
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import AppointmentService from '../../services/AppointmentService';
import { green } from '@mui/material/colors';
import { useSelector } from 'react-redux';
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { selectSelectedServiceProvider } from '../../stateManagement/selectedServiceProviderSlice';

const appointmentSchema = z.object({
    appointmentId: z.string(),
    version: z.number(),
    serviceProviderId: z.string(),
    note: z.string().max(200, "Maximum length of Note is 200 characters.").optional(),
    startDateTime: z.string(),
    endDateTime: z.string().optional(),
});

interface AppointmentFormProps {
    closeForm: (input?: IAppointment) => void;
    updateExistData: (input: IAppointment) => void;
    appointment: IAppointment;
    disabled: boolean;
}

const AppointmentNoteForm: React.FC<AppointmentFormProps> = ({ closeForm, appointment, updateExistData, disabled }) => {
    const { enqueueSnackbar } = useSnackbar();


    const selectedOrganization = useSelector(selectSelectedOrganization);
    const selectedServiceProvider = useSelector(selectSelectedServiceProvider);

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<AppointmentUpdateNoteRequest>({
        resolver: zodResolver(appointmentSchema),
        defaultValues: {
            appointmentId: '',
            serviceProviderId: selectedServiceProvider.serviceProviderId,
            note: '',
            version: 0,
            startDateTime: '',
            endDateTime: '',
        },
    });


    useEffect(() => {
        if (appointment) {
            reset({
                appointmentId: appointment.appointmentId,
                serviceProviderId: appointment.serviceProviderId,
                note: appointment.note,
                version: appointment.version,
                startDateTime: appointment.startDateTime,
                endDateTime: appointment.endDateTime,
            });

        }
    }, [appointment, reset]);

    const [noteLoading, setNoteLoading] = useState(false);
    const handleSave = async (input: AppointmentUpdateNoteRequest) => {
        setNoteLoading(true);

        try {

            const response = await AppointmentService.UpdateNote(selectedOrganization.organizationId, selectedServiceProvider.serviceProviderId, input);

            updateExistData({ ...appointment, note: response.note, version: response.version });
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setNoteLoading(false);
        }
    };

    const note = watch('note');

    const isDirty =
        note !== appointment.note

    return (
        <form onSubmit={handleSubmit(handleSave)} noValidate>
            <Box display="flex" alignItems="center" gap={2} mt={2}>
                <TextField fullWidth label="Note" InputLabelProps={{ shrink: true }} multiline rows={3} {...register('note')} error={!!errors.note} helperText={errors.note?.message} disabled={noteLoading || disabled} />
                <Box sx={{ position: 'relative' }}>
                    <Fab disabled={noteLoading || !isDirty || disabled} size='small' aria-label="save" color="primary" type="submit">
                        {!isDirty ? <CheckIcon /> : <SaveIcon />}
                    </Fab>
                    {noteLoading && (
                        <CircularProgress
                            size={48}
                            sx={{
                                color: green[500],
                                position: 'absolute',
                                top: -4,
                                left: -4,
                                zIndex: 1,
                            }}
                        />
                    )}
                </Box>
            </Box>
        </form>
    );
};

export default AppointmentNoteForm;